import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchCollection, getOptimizedImageUrl } from '../lib/cms';
import Map from '../components/Map';
import ContactForm from '../components/ContactForm';
import { 
  Settings, 
  Zap, 
  RefreshCw, 
  TrendingUp, 
  Globe, 
  Truck, 
  ArrowRight, 
  ShieldCheck, 
  Flame, 
  Anchor, 
  Activity,
  ChevronRight,
  Phone,
  Mail
} from 'lucide-react';

export default function Home({ isEn }) {
  const [pageData, setPageData] = useState(null);
  const [latestCases, setLatestCases] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('index');
        const cases = await fetchCollection('cases', { limit: 3 });
        const news = await fetchCollection('news', { limit: 2 });
        
        setPageData(page);
        setLatestCases(cases);
        setLatestNews(news);
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const lang = isEn ? 'en' : 'da';

  // Fallback indhold hvis CMS ikke svarer eller er tomt
  const titleText = pageData?.title?.[lang] || 'Fredericia Isolerings-puder';
  const seoTitle = pageData?.seo_title?.[lang] || 'Fredericia Isolerings-puder | Bæredygtige løsninger';
  const seoDesc = pageData?.seo_description?.[lang] || 'Skræddersyede isoleringspuder til industri og marine produceret med 100% grøn energi.';

  const benefits = isEn ? [
    {
      title: 'Customized Production',
      desc: 'Insulation pads are produced specifically for the object to be insulated. No standard solutions.',
      icon: Settings
    },
    {
      title: 'Fast Installation',
      desc: 'Designed for rapid mounting and dismounting, reducing downtime significantly.',
      icon: Zap
    },
    {
      title: 'Easy Maintenance',
      desc: 'Simplifies service and repair of machinery as pads can be removed and reused in minutes.',
      icon: RefreshCw
    },
    {
      title: 'Energy Savings',
      desc: 'Drastically reduces heat loss, gas and energy consumption, leading to lower costs.',
      icon: TrendingUp
    },
    {
      title: 'Global Experience',
      desc: 'Proven track record of delivering high-quality insulation solutions worldwide.',
      icon: Globe
    },
    {
      title: 'Space-Saving Logistics',
      desc: 'Extremely compact to transport compared to traditional metal cladding.',
      icon: Truck
    }
  ] : [
    {
      title: 'Skræddersyet produktion',
      desc: 'Isoleringspuder produceres specifikt til det emne, der skal isoleres. Ingen standardløsninger.',
      icon: Settings
    },
    {
      title: 'Hurtig installation',
      desc: 'Designet til lynhurtig montering og afmontering, hvilket reducerer driftstop markant.',
      icon: Zap
    },
    {
      title: 'Nem afmontering',
      desc: 'Gør service og reparation af maskiner lettere, da puderne kan genbruges igen og igen.',
      icon: RefreshCw
    },
    {
      title: 'Energibesparelse',
      desc: 'Reducerer varmetab, gas- og energiforbrug drastisk, hvilket sikrer lavere omkostninger.',
      icon: TrendingUp
    },
    {
      title: 'Global erfaring',
      desc: 'Dokumenteret erfaring med levering af højkvalitets isoleringsløsninger til hele verden.',
      icon: Globe
    },
    {
      title: 'Pladsbesparende transport',
      desc: 'Ekstremt kompakte at transportere sammenlignet med traditionelle metalkasser.',
      icon: Truck
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex flex-col lg:flex-row bg-brand-dark overflow-hidden border-b border-gray-800">
        {/* Tech grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:5rem_5rem] opacity-15 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_100%_200px,rgba(249,115,22,0.08),transparent_60%)] pointer-events-none" />
        
        {/* Hero Text */}
        <div className="w-full lg:w-1/2 flex items-center py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 xl:pl-[calc((100vw-1280px)/2+2rem)] relative z-10">
          <div className="max-w-xl lg:max-w-lg xl:max-w-xl space-y-6 text-left">
            <div className="inline-flex items-center gap-2 text-xs font-mono text-white uppercase tracking-wider">
              {isEn ? '100% Green Energy Production' : 'Fremstillet af 100% grøn energi'}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white tracking-tight leading-tight">
              {isEn ? 'Tailor-Made' : 'Skræddersyede'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-orange-400">
                {isEn ? 'Insulation Pads' : 'Isoleringspuder'}
              </span><br />
              {isEn ? 'for Industry & Marine' : 'til Industri & Marine'}
            </h1>
            
            <p className="text-[#f1f1f1] text-base sm:text-lg max-w-xl leading-relaxed">
              {isEn 
                ? 'High-temperature insulation solutions designed specifically for your equipment. Reduce energy loss, prevent fires, and cut downtime with reusable pads.'
                : 'Højtemperatur isoleringsløsninger designet specifikt til dit udstyr. Reducer energitab, forebyg brand og minimer driftstop med genanvendelige puder.'}
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to={isEn ? '/en/contact' : '/kontakt'}
                className="bg-brand-accent hover:bg-brand-accent-hover text-white font-medium px-6 py-3.5 transition-colors flex items-center gap-2 shadow-lg shadow-brand-accent/10"
              >
                {isEn ? 'Get a Quote' : 'Få et uforpligtende tilbud'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Quick stats / highlights */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-800/60 max-w-lg">
              <div>
                <div className="font-display font-bold text-2xl text-white">1.300°C</div>
                <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">{isEn ? 'Max Temp' : 'Max temp'}</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-white">100%</div>
                <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">{isEn ? 'Custom' : 'Skræddersyet'}</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-white">Minutes</div>
                <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">{isEn ? 'Installation' : 'Montering'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Image (fills entire side) */}
        <div className="w-full lg:w-1/2 relative min-h-[350px] lg:min-h-[70vh] self-stretch overflow-hidden">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/industriisolering-15-scaled.jpg', { width: 1200 })}
            alt="Fredericia Isoleringspuder"
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-brand-dark via-transparent to-transparent opacity-80 lg:opacity-60" />
        </div>
      </section>

      {/* Sustainable Focus Intro Section */}
      <section className="py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-display font-bold text-black tracking-tight">
                {isEn ? 'Bespoke Solutions & Sustainability' : 'Bæredygtige løsninger & specialproduktion'}
              </h2>
              <div className="text-gray-600 space-y-4 leading-relaxed text-sm sm:text-base">
                <p>
                  {isEn 
                    ? 'At Fredericia Isoleringspuder, we design custom insulation solutions that optimize energy efficiency and reduce heat loss. All our insulation pads are manufactured with precision to fit your specific industrial or marine applications.'
                    : 'Hos Fredericia Isoleringspuder designer vi skræddersyede isoleringsløsninger, der optimerer energieffektiviteten og reducerer varmetab. Alle vores isoleringspuder fremstilles med præcision til jeres specifikke behov.'}
                </p>
              </div>
              <div className="pt-2">
                <Link
                  to={isEn ? '/en/about' : '/om'}
                  className="inline-flex items-center gap-2 border border-black text-black hover:bg-black hover:text-white font-display font-bold uppercase tracking-wider text-xs px-6 py-3.5 transition-colors"
                >
                  {isEn ? 'Read More About Us' : 'Læs mere om os'}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <div className="bg-white border border-gray-200 rounded-none p-6 space-y-3 shadow-sm">
                <div className="h-10 w-10 flex items-center justify-center text-black">
                  <Settings className="h-5 w-5" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-base">{isEn ? 'No Standard Solutions' : 'Ingen standardløsninger'}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {isEn 
                    ? 'Every insulation pad is engineered specifically for the exact application, temperature conditions, and installation requirements.'
                    : 'Hver isoleringspude er konstrueret specifikt til den præcise anvendelse, temperaturforhold og installationskrav.'}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-none p-6 space-y-3 shadow-sm">
                <div className="h-10 w-10 flex items-center justify-center text-black">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-base">{isEn ? '100% Green Energy' : '100% grøn energi'}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {isEn 
                    ? 'Since June 1, 2023, all our insulation pads are produced exclusively using green energy from our own solar plant.'
                    : 'Siden 1. juni 2023 produceres alle vores isoleringspuder udelukkende med grøn strøm fra vores eget solcelleanlæg.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-brand-dark border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Left-aligned header block */}
          <div className="text-left max-w-3xl mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight">
              {isEn ? 'Why Choose Insulation Pads?' : 'Hvorfor vælge isoleringspuder?'}
            </h2>
            <p className="text-[#f1f1f1] text-sm sm:text-base leading-relaxed mt-6">
              {isEn 
                ? 'Discover the practical and financial advantages of our customized insulation pads over traditional metal cladding.'
                : 'Oplev de praktiske og økonomiske fordele ved vores skræddersyede isoleringspuder frem for traditionel metalindkapsling.'}
            </p>
          </div>

          {/* 4-column feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.slice(0, 4).map((benefit, idx) => {
              return (
                <div 
                  key={idx} 
                  className="border-l-2 border-brand-accent pl-6 py-2 flex flex-col justify-between"
                >
                  <div>
                    <h3 className="font-display font-bold uppercase tracking-wider text-white text-base">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-[#f1f1f1] leading-relaxed mt-3">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Logistics Storytelling Section */}
      <section className="py-20 bg-gray-50 border-b border-gray-200 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6 text-left">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 tracking-tight leading-tight">
              {isEn ? 'Fast and Space-Saving Logistics' : 'Hurtig og pladsbesparende logistik'}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              {isEn 
                ? 'Traditional metal cladding is bulky, heavy, and extremely difficult to transport. Our insulation pads are flexible, lightweight, and can be compressed.'
                : 'Traditionel metalindkapsling er tung, fylder enormt meget og er svær at transportere. Vores isoleringspuder er fleksible, lette og kan pakkes tæt.'}
            </p>

            {/* Storytelling comparison */}
            <div className="space-y-4 pt-2">
              <div className="flex gap-4 items-start">
                <div className="text-black shrink-0 mt-1 font-mono text-sm font-bold">1.</div>
                <div>
                  <h4 className="text-gray-900 font-semibold text-sm">{isEn ? 'Unbeatable Transport Capacity' : 'Enorm transportfordel'}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {isEn 
                      ? 'What requires an entire truck of metal cladding can fit into a single small delivery van when using flexible insulation pads.'
                      : 'Hvad der kræver en hel lastbil af traditionelle metalkasser, kan transporteres i én enkelt lille varevogn med vores fleksible isoleringspuder.'}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="text-black shrink-0 mt-1 font-mono text-sm font-bold">2.</div>
                <div>
                  <h4 className="text-gray-900 font-semibold text-sm">{isEn ? 'Rapid Installation & Dismounting' : 'Lynhurtig montering & afmontering'}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {isEn 
                      ? 'Installation takes minutes instead of hours. When machinery needs service, pads are easily unstrapped and reattached, reducing downtime.'
                      : 'Installationen tager minutter i stedet for timer. Når maskiner skal serviceres, spændes puderne blot af og på igen, hvilket minimerer driftstop.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 space-y-6 shadow-sm">
              <h3 className="font-display font-bold text-gray-900 text-lg tracking-tight border-b border-gray-100 pb-4">
                {isEn ? 'Logistics Comparison' : 'Logistisk sammenligning'}
              </h3>
              
              <div className="space-y-4">
                {/* Traditional */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-mono uppercase tracking-wider text-gray-400">
                    <span>{isEn ? 'Traditional Metal Cladding' : 'Traditionel metalindkapsling'}</span>
                    <span className="text-red-500">100% {isEn ? 'Bulk' : 'Volumen'}</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500/60 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>

                {/* Insulation pads */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-mono uppercase tracking-wider text-gray-500">
                    <span className="text-gray-900 font-semibold">{isEn ? 'FI Insulation Pads' : 'FI Isoleringspuder'}</span>
                    <span className="text-brand-accent font-bold">15% {isEn ? 'Bulk' : 'Volumen'}</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-accent rounded-full" style={{ width: '15%' }} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-xs text-gray-500 font-mono uppercase tracking-wider mb-1">{isEn ? 'Transport Space' : 'Transportplads'}</div>
                  <div className="text-lg font-bold text-gray-900">{isEn ? '85% Saved' : '85% sparet'}</div>
                </div>
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-xs text-gray-500 font-mono uppercase tracking-wider mb-1">{isEn ? 'Installation Time' : 'Monteringstid'}</div>
                  <div className="text-lg font-bold text-gray-900">{isEn ? '90% Faster' : '90% hurtigere'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry & Marine Entrances */}
      <section className="py-20 bg-brand-dark border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Our Two Core Business Areas' : 'Vores to primære forretningsområder'}
            </h2>
            <p className="text-gray-400 text-sm sm:text-base mt-2">
              {isEn 
                ? 'We specialize in tailored insulation solutions across two main sectors, each with unique requirements.'
                : 'Vi har specialiseret os i skræddersyede isoleringsløsninger på tværs af to hovedområder med hver deres unikke krav.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Industri Entrance */}
            <div className="bg-brand-surface border border-gray-800 rounded-xl overflow-hidden group hover:border-gray-700 transition-all duration-300 flex flex-col">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/industriisolering-9-scaled.jpg', { width: 600 })}
                  alt="Industriisolering"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-transparent to-transparent opacity-80" />
              </div>
              <div className="p-6 sm:p-8 flex-grow flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <h3 className="font-display font-bold text-2xl text-white group-hover:text-brand-accent transition-colors">
                    {isEn ? 'Industrial Insulation' : 'Industri'}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {isEn 
                      ? 'Optimize energy efficiency, reduce heat loss, and cut gas consumption in process plants, CHP plants, and factories.'
                      : 'Optimer energieffektiviteten, reducer varmetab og sænk gasforbruget i procesanlæg, kraftvarmeværker og fabrikker.'}
                  </p>
                  <ul className="grid grid-cols-2 gap-2 pt-2 text-xs text-gray-400 font-mono">
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Energy Saving' : 'Energibesparelse'}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Lower Costs' : 'Lavere omkostninger'}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Process Control' : 'Procesoptimering'}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'CO2 Reduction' : 'CO2-reduktion'}
                    </li>
                  </ul>
                </div>
                <Link
                  to={isEn ? '/en/industry' : '/industri'}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-brand-accent transition-colors"
                >
                  {isEn ? 'Explore Industrial Solutions' : 'Læs mere om industri'}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Marine Entrance */}
            <div className="bg-brand-surface border border-gray-800 rounded-xl overflow-hidden group hover:border-gray-700 transition-all duration-300 flex flex-col">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/marine-custom.jpg', { width: 600 })}
                  alt="Marineisolering"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-transparent to-transparent opacity-80" />
              </div>
              <div className="p-6 sm:p-8 flex-grow flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <h3 className="font-display font-bold text-2xl text-white group-hover:text-brand-accent transition-colors">
                    {isEn ? 'Marine Insulation' : 'Marine'}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {isEn 
                      ? 'Protect equipment, safeguard personnel, and prevent engine fires in harsh offshore environments and ship engine rooms.'
                      : 'Beskyt udstyr, værn om personalets sikkerhed og forebyg motorbrand under barske offshore-forhold og i maskinrum på skibe.'}
                  </p>
                  <ul className="grid grid-cols-2 gap-2 pt-2 text-xs text-gray-400 font-mono">
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Fire Prevention' : 'Brandforebyggelse'}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Personnel Safety' : 'Personlig sikkerhed'}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Equipment Protection' : 'Udstyrsbeskyttelse'}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-accent" />
                      {isEn ? 'Marine Approved' : 'Marinegodkendt'}
                    </li>
                  </ul>
                </div>
                <Link
                  to={isEn ? '/en/marine' : '/marine'}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-brand-accent transition-colors"
                >
                  {isEn ? 'Explore Marine Solutions' : 'Læs mere om marine'}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      {latestCases.length > 0 && (
        <section className="py-20 bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 gap-4">
              <div>
                <h2 className="text-3xl font-display font-bold text-gray-900 tracking-tight">
                  {isEn ? 'Featured Case Studies' : 'Udvalgte cases'}
                </h2>
              </div>
              <Link
                to={isEn ? '/en/cases' : '/cases'}
                className="inline-flex items-center gap-1 text-sm font-semibold text-brand-accent hover:text-brand-accent-hover transition-colors"
              >
                {isEn ? 'View all cases' : 'Se alle cases'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestCases.map((item) => (
                <Link
                  key={item.id}
                  to={isEn ? `/en/cases/${item.url_slug}` : `/cases/${item.url_slug}`}
                  className="bg-white border border-gray-200 rounded-none overflow-hidden group flex flex-col shadow-sm"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                    {item.images?.[0] ? (
                      <img
                        src={getOptimizedImageUrl(item.images[0], { width: 400 })}
                        alt={item.title?.[lang]}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 font-mono text-xs">
                        {isEn ? 'No Image' : 'Intet billede'}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-grow flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h3 className="font-display font-bold text-lg text-gray-900 group-hover:text-black transition-colors line-clamp-2">
                        {item.title?.[lang]}
                      </h3>
                      <div 
                        className="text-xs text-gray-600 line-clamp-3 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.description?.[lang] || '' }}
                      />
                    </div>
                    <div className="text-xs font-semibold text-black flex items-center gap-1 group-hover:text-gray-600 transition-colors">
                      {isEn ? 'Read Case Study' : 'Læs hele casen'}
                      <ChevronRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA / Contact Section */}
      <section className="py-20 bg-brand-surface relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_0%_100%,rgba(249,115,22,0.05),transparent_60%)]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 space-y-6 text-left">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight leading-tight">
              {isEn ? 'Ready to Optimize Your Equipment?' : 'Klar til at optimere dit udstyr?'}
            </h2>
            <p className="text-[#f1f1f1] text-sm sm:text-base leading-relaxed">
              {isEn 
                ? 'Contact us today for a non-binding dialogue about your insulation needs, or to get a customized quote for your project.'
                : 'Kontakt os i dag for en uforpligtende dialog om dine isoleringsbehov, eller for at få et skræddersyet tilbud til dit projekt.'}
            </p>

            <div className="space-y-4 pt-4 border-t border-gray-800/60">
              <div className="flex items-center gap-3">
                <div className="text-brand-accent shrink-0">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <a href="tel:+4552408402" className="text-sm sm:text-base font-semibold text-white hover:text-brand-accent transition-colors font-mono">
                    +45 52 40 84 02
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-brand-accent shrink-0">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <a href="mailto:fr@isoleringspuder.dk" className="text-sm sm:text-base font-semibold text-white hover:text-brand-accent transition-colors font-mono">
                    fr@isoleringspuder.dk
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm isEn={isEn} />
          </div>
        </div>
      </section>
    </>
  );
}
